import * as MailActions from '../actions/mail';
import { reducer } from './reducerUtils';

const initialState = {
  activeEmail: '',
  draftId: null,
  emails: {},
  emailBody: '',
  fetchingEmail: false,
  fetchingRecentRecords: false,
  isComposingEmail: false,
  autoThreadingDefault: false,
  isEmailSaved: {},
  isSavingDocrioEml: false,
  isSavingToTimeline: false,
  isSavingToTimelineByDefault: false,
  recentRecords: [],
  listViews: [],
  selectedRecords: {},
  emailSubject: '',
  timelineSubject: '',
  docrioSubject: '',
  error: '',
  icons: [],
  isChangingEmailSaveLocation: false,
  presave: false,
  isEmailEdited: false,
  isSubjectEdited: false,
  autoThreadingToggled: false,
  savingToTimelineToggled: false,
  removalJobId: '',
  isRemovingSaveLocation: false,
  emailSalesforceIds: [],
  isSearchingRelatedRecords: false,
  ownerEmail: null,
  ownerDisplayName: null,
  delegateError: false,
  initialEmailAutothreadedRecords: [],
};

export default reducer({
  [MailActions.COMPOSING_EMAIL]: (state, action) => ({
    ...state,
    isComposingEmail: true,
    draftId: action.payload.draftId,
    presave: action.payload.presave,
  }),
  [MailActions.EMAIL_LOADED]: (state, action) => {
    const { emails } = action;
    const { itemId } = emails;
    return {
      ...state,
      activeEmail: itemId,
      emails: {
        ...emails,
        [itemId]: {
          ...state.emails[itemId],
        },
      },
      isComposingEmail: false,
    };
  },
  [MailActions.AUTO_THREADING_DEFAULT]: (state, action) => ({
    ...state,
    autoThreadingDefault: action.status,
  }),
  [MailActions.GET_EMAIL]: (state) => ({
    ...state,
    fetchingEmail: true,
  }),
  [MailActions.EMAIL_SAVED_STATUS]: (state, action) => ({
    ...state,
    isEmailSaved: action.savedStatus,
    fetchingEmail: false,
    recentRecords: action.recentRecords,
    listViews: action.listViews,
  }),
  [MailActions.IS_ACTIVITY_TIMELINE_SAVED]: (state, { savedStatus }) => ({
    ...state,
    isEmailSaved: savedStatus, // Saved status should be { <recordId>: boolean, <recordId>: boolean, ... }
  }),
  [MailActions.SELECT_RECORDS]: (state, { selectedRecords }) => ({
    ...state,
    selectedRecords: updateSelectedRecords(selectedRecords),
  }),
  [MailActions.UNSELECT_RECORD]: (state, { record }) => ({
    ...state,
    selectedRecords: removeSelectedRecord(state, record),
    isSavingDocrioEml: false,
    docrioSubject: '',
    timelineSubject: '',
    isEmailSaved: removeIsEmailSavedRecord(state, record.Id),
  }),
  [MailActions.UPDATE_RECORDS]: (state, { records }) => ({
    ...state,
    selectedRecords: { ...state.selectedRecords, ...records.reduce((acc, curValue) => ({ ...acc, [curValue.Id]: curValue }), {}) },
  }),

  [MailActions.LOAD_SETTINGS]: (state, { settings }) => ({
    ...state,
    settings,
  }),
  [MailActions.SAVING_TO_TIMELINE]: (state, action) => ({
    ...state,
    isSavingToTimeline: action.status,
  }),
  [MailActions.SAVING_TO_TIMELINE_DEFAULT]: (state, action) => ({
    ...state,
    isSavingToTimelineByDefault: action.status,
  }),
  [MailActions.SAVE_EMAIL_SUCCESS]: (state) => ({
    ...state,
    isEmailSaved: updateIsEmailSaved(state, true),
    isEmailEdited: false,
  }),
  [MailActions.SAVE_EMAIL_FAILURE]: (state, action) => ({
    ...state,
    error: action.payload.error,
  }),
  [MailActions.SET_SUBJECT]: (state, action) => ({
    ...state,
    emailSubject: action.subject,
  }),
  [MailActions.SET_BODY]: (state, action) => ({
    ...state,
    emailBody: action.emailBody,
  }),
  [MailActions.SET_TIMELINE_SUBJECT]: (state, action) => ({
    ...state,
    timelineSubject: action.subject,
  }),
  [MailActions.SET_DOCRIO_SUBJECT]: (state, action) => ({
    ...state,
    docrioSubject: action.subject,
  }),
  [MailActions.LOAD_ICONS]: (state, action) => ({
    ...state,
    icons: action.icons,
  }),
  [MailActions.IS_CHANGING_EMAIL_SAVE_LOCATION]: (state, action) => ({
    ...state,
    isChangingEmailSaveLocation: action.payload,
  }),
  [MailActions.IS_EMAIL_EDITED]: (state, action) => ({
    ...state,
    isEmailEdited: action.status,
  }),
  [MailActions.IS_SUBJECT_EDITED]: (state, action) => ({
    ...state,
    isSubjectEdited: action.status,
  }),
  [MailActions.IS_PRESAVE]: (state, action) => ({
    ...state,
    presave: action.status,
  }),
  [MailActions.AUTO_THREADING_TOGGLED]: (state, action) => ({
    ...state,
    autoThreadingToggled: action.payload,
  }),
  [MailActions.SAVING_TO_TIMELINE_TOGGLED]: (state, action) => ({
    ...state,
    savingToTimelineToggled: action.payload,
  }),
  [MailActions.SET_REMOVAL_JOB_ID]: (state, action) => ({
    ...state,
    removalJobId: action.jobId,
  }),
  [MailActions.IS_REMOVING_SAVE_LOCATION]: (state, action) => ({
    ...state,
    isRemovingSaveLocation: action.status,
  }),
  [MailActions.SET_EMAIL_SALESFORCE_IDS]: (state, action) => ({
    ...state,
    emailSalesforceIds: action.emailSalesforceIds,
  }),
  [MailActions.SET_IS_SEARCHING_RELATED_RECORDS]: (state, action) => ({
    ...state,
    isSearchingRelatedRecords: action.status,
  }),
  [MailActions.SET_OWNER_EMAIL]: (state, action) => ({
    ...state,
    ownerEmail: action.ownerEmail,
  }),
  [MailActions.SET_OWNER_DISPLAY_NAME]: (state, action) => ({
    ...state,
    ownerDisplayName: action.ownerDisplayName,
  }),
  [MailActions.SET_DELEGATE_ERROR]: (state, action) => ({
    ...state,
    delegateError: true,
  }),
  [MailActions.SET_INITIAL_EMAIL_AUTO_THREADED_RECORDS]: (state, action) => ({
    ...state,
    initialAutothreadedRecords: action.payload,
  }),
}, initialState);

const updateIsEmailSaved = (state, isSaved) => {
  const newIsEmailSaved = {};
  Object.keys?.(state.selectedRecords).forEach((recordId) => {
    newIsEmailSaved[recordId] = state.selectedRecords[recordId]?.emailMessageError ? false : isSaved;
  });

  return newIsEmailSaved;
};

const updateSelectedRecords = (selectedRecords) => {
  const newSelectedRecords = {};
  selectedRecords.forEach((record) => {
    newSelectedRecords[record.Id] = record;
  });
  return newSelectedRecords;
};

const removeSelectedRecord = (state, record) => {
  const newSelectedRecords = { ...state.selectedRecords };
  delete newSelectedRecords[record.Id];
  return newSelectedRecords;
};

const removeIsEmailSavedRecord = (state, recordId) => {
  const isEmailSaved = { ...state.isEmailSaved };
  delete isEmailSaved[recordId];
  return isEmailSaved;
};
